// Helper function to convert "HH:MM" to minutes since midnight
import { t } from "i18next";
import { isNilOrEmpty } from "ramda-adjunct";
import { array, boolean, InferType, number, object, string } from "yup";

function timeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
}

export const timeRange = object({
  from: string().defined().default("00:00"),
  to: string()
    .defined()
    .default("23:59")
    .test(
      "is-after-from",
      () => t("endTimeBeforeStart"),
      function (value) {
        const { from } = this.parent;
        if (!from || !value) return true; // Skip validation if either value is missing

        // Convert time strings to comparable format (minutes since midnight)
        const fromMinutes = timeToMinutes(from);
        const toMinutes = timeToMinutes(value);

        return toMinutes > fromMinutes || (toMinutes === 0 && toMinutes !== fromMinutes);
      }
    ),
});
export const weekDaySchema = object({
  weekDay: number().min(1).max(7).required(),
  enabled: boolean().default(false),
  times: array()
    .of(timeRange)
    .test(
      "no-overlapping-intervals",
      () => t("timeOverlapping"),
      function (value) {
        if (isNilOrEmpty(value)) return true; // No overlap possible with 0 or 1 intervals

        return !value?.some((interval) => {
          const { from, to } = interval;
          const fromMinutes = timeToMinutes(from);
          const toMinutes = timeToMinutes(to);
          return value?.some((otherInterval) => {
            const { from: otherFrom, to: otherTo } = otherInterval;
            const otherFromMinutes = timeToMinutes(otherFrom);
            const otherToMinutes = timeToMinutes(otherTo);
            return interval !== otherInterval && otherToMinutes >= fromMinutes && otherFromMinutes <= toMinutes;
          });
        });
      }
    )
    .default([]),
});
export const intervalTypeTimeSchema = object({
  daysOfWeek: array()
    .of(weekDaySchema)
    .transform((value: InferType<typeof weekDaySchema>[]) => {
      if (Array.isArray(value)) {
        return value.filter((item) => item.enabled);
      }
      return value;
    })
    .default(
      Array.from({ length: 7 }, (_, i) => ({
        ...weekDaySchema.getDefault(),
        weekDay: i + 1,
      }))
    ),
});
